import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../components/ConfirmModal';
import SmartPagination from '../components/SmartPagination';
import { sinistresAPI } from '../services/api';
import AISinistreModal from '../components/AISinistreModal';
import Plate from '../components/Plate';
import { Plus, Search, Eye, Pencil, Trash2, FileText, RefreshCw, Users, Car, FileCheck, Sparkles } from 'lucide-react';
import { playSound } from '../utils/SoundPlayer';
import toast from 'react-hot-toast';


const formatDate = (val) => {
    if (!val) return '—';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && val.includes('-')) {
        const parts = val.split('T')[0].split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return val;
        return d.toLocaleDateString('fr-DZ');
    } catch { return val; }
};

const UserAvatar = ({ name }) => {
    const initials = name
        ? name.trim().split(/\s+/).map(n => n[0]).slice(0, 2).join('').toUpperCase()
        : '?';
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    const charCode = name ? name.charCodeAt(0) : 0;
    const bgColor = colors[charCode % colors.length];
    return (
        <div style={{
            width: '32px', height: '32px', borderRadius: '50%', background: bgColor,
            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', fontWeight: '700', marginRight: '10px', flexShrink: 0
        }}>
            {initials}
        </div>
    );
};

// Local Plate component removed in favor of shared component

const SinistresPage = () => {
    const navigate = useNavigate();
    const [sinistres, setSinistres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statut, setStatut] = useState('');
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [assurance, setAssurance] = useState('');
    const [marque, setMarque] = useState('');
    const [modele, setModele] = useState('');
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
    const [stats, setStats] = useState({ totalClients: 0, totalPV: 0 });
    const [aiModalOpen, setAiModalOpen] = useState(false);

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({ open: false, id: null, num: null, loading: false });

    const fetchSinistres = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const res = await sinistresAPI.list({ 
                page, 
                limit: 20, 
                search, 
                statut,
                date_start: dateStart,
                date_end: dateEnd,
                assurance,
                marque,
                modele
            });
            setSinistres(res.data.data || []);
            setPagination(res.data.pagination || { total: 0, page: 1, limit: 20, pages: 1 });
            if (res.data.stats) setStats(res.data.stats || { totalClients: 0, totalPV: 0 });
        } catch {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [search, statut, dateStart, dateEnd, assurance, marque, modele]);

    useEffect(() => { fetchSinistres(1); }, [fetchSinistres]);

    const handleDeleteRequest = (id, num) => {
        setConfirmModal({ open: true, id, num, loading: false });
    };

    const handleDeleteConfirm = async () => {
        setConfirmModal(m => ({ ...m, loading: true }));
        try {
            playSound('delete');
            await sinistresAPI.delete(confirmModal.id);
            toast.success('Dossier supprimé');
            setConfirmModal({ open: false, id: null, num: null, loading: false });
            fetchSinistres(pagination.page);
        } catch {
            toast.error('Erreur lors de la suppression');
            setConfirmModal(m => ({ ...m, loading: false }));
        }
    };


    const encodeId = (id) => encodeURIComponent(id);

    return (
        <Layout title="Sinistres & Clients">
            {/* Confirm Delete Modal */}
            <ConfirmModal
                open={confirmModal.open}
                title="Supprimer le Sinistré"
                message={`Voulez-vous vraiment supprimer le sinistré ${confirmModal.num} ? Cette action est irréversible et supprimera également les données associées.`}
                confirmLabel="Oui, supprimer"
                cancelLabel="Annuler"
                variant="danger"
                loading={confirmModal.loading}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirmModal({ open: false, id: null, num: null, loading: false })}
            />

            {/* Header */}
            <div className="flex-between mb-6">
                <div>
                    <h2 style={{ fontSize: '24px', fontWeight: '800', color: 'var(--primary-800)', letterSpacing: '-0.5px' }}>Dossier Sinistres</h2>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Gérez votre portefeuille client et l'ensemble des missions d'expertise</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button 
                        className="btn btn-outline" 
                        onClick={() => setAiModalOpen(true)}
                        style={{ 
                            borderColor: '#a855f7', 
                            color: '#a855f7',
                            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Sparkles size={18} /> Sinistré par IA
                    </button>
                    <button className="btn btn-primary" onClick={() => navigate('/sinistres/nouveau')} style={{ boxShadow: '0 4px 12px rgba(37, 99, 168, 0.3)' }}>
                        <Plus size={18} /> Nouveau Sinistré
                    </button>
                </div>
            </div>

            {/* Quick Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div className="card" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px', border: 'none', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ background: 'var(--primary-50)', color: 'var(--primary-600)', padding: '10px', borderRadius: '12px' }}><Car size={18} /></div>
                    <div><div style={{ fontSize: '22px', fontWeight: '800' }}>{pagination.total}</div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' }}>Total Sinistrés</div></div>
                </div>
                <div className="card" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px', border: 'none', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ background: '#ecfdf5', color: '#10b981', padding: '10px', borderRadius: '12px' }}><Users size={18} /></div>
                    <div><div style={{ fontSize: '22px', fontWeight: '800' }}>{stats.totalClients}</div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' }}>Clients Uniques</div></div>
                </div>
                <div className="card" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px', border: 'none', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ background: '#eff6ff', color: '#2563a8', padding: '10px', borderRadius: '12px' }}><FileCheck size={18} /></div>
                    <div><div style={{ fontSize: '22px', fontWeight: '800' }}>{stats.totalPV}</div><div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' }}>PV Finalisés</div></div>
                </div>
            </div>

            <div className="table-container" style={{ border: 'none', boxShadow: 'var(--shadow)', borderRadius: 'var(--radius-lg)' }}>
                {/* Filters */}
                <div className="table-header" style={{ padding: '20px', flexDirection: 'column', alignItems: 'stretch', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="search-box" style={{ flex: 1, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                            <Search size={16} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Rechercher par Assuré, N° PV, Réf. Interne, Immat..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <select
                            className="form-control"
                            style={{ maxWidth: '180px', height: '42px', fontSize: '13px' }}
                            value={statut}
                            onChange={(e) => setStatut(e.target.value)}
                        >
                            <option value="">Tous les statuts</option>
                            <option value="brouillon">📝 Brouillons</option>
                            <option value="en_cours">⌛ En cours</option>
                            <option value="termine">✅ Terminés</option>
                        </select>
                        <button className="btn btn-ghost" onClick={() => fetchSinistres(pagination.page)} title="Actualiser">
                            <RefreshCw size={16} />
                        </button>
                    </div>

                    {/* Advanced Filters Row */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Assurance:</span>
                            <input 
                                type="text" className="form-control" placeholder="Nom..."
                                style={{ width: '130px', height: '36px', fontSize: '12px' }}
                                value={assurance} onChange={e => setAssurance(e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Date Accident:</span>
                            <input 
                                type="date" className="form-control"
                                style={{ width: '130px', height: '36px', fontSize: '12px' }}
                                value={dateStart} onChange={e => setDateStart(e.target.value)}
                            />
                            <span style={{ color: 'var(--text-muted)' }}>au</span>
                            <input 
                                type="date" className="form-control"
                                style={{ width: '130px', height: '36px', fontSize: '12px' }}
                                value={dateEnd} onChange={e => setDateEnd(e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Véhicule:</span>
                            <input 
                                type="text" className="form-control" placeholder="Marque..."
                                style={{ width: '100px', height: '36px', fontSize: '12px' }}
                                value={marque} onChange={e => setMarque(e.target.value)}
                            />
                            <input 
                                type="text" className="form-control" placeholder="Modèle..."
                                style={{ width: '100px', height: '36px', fontSize: '12px' }}
                                value={modele} onChange={e => setModele(e.target.value)}
                            />
                        </div>
                        {(dateStart || dateEnd || assurance || marque || modele) && (
                            <button 
                                className="btn btn-ghost btn-sm" 
                                onClick={() => { setDateStart(''); setDateEnd(''); setAssurance(''); setMarque(''); setModele(''); }}
                                style={{ color: 'var(--danger)', fontSize: '11px', padding: '4px 8px' }}
                            >
                                Reinitialiser
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="loading-spinner" style={{ padding: '60px' }}><div className="spinner" /></div>
                ) : sinistres.length === 0 ? (
                    <div className="empty-state" style={{ padding: '80px' }}>
                        <div style={{ width: '80px', height: '80px', background: 'var(--surface-2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <Search size={32} color="var(--text-muted)" />
                        </div>
                        <h3>Aucun sinistré trouvé</h3>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>Essayez d'ajuster vos filtres ou créez une nouvelle mission.</p>
                        <button className="btn btn-primary" onClick={() => navigate('/sinistres/nouveau')}>
                            <Plus size={16} /> Nouveau Sinistré
                        </button>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                            <thead>
                                <tr>
                                    <th style={{ padding: '12px 20px', background: 'var(--surface-3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>RÉF. SINISTRÉ</th>
                                    <th style={{ padding: '12px 20px', background: 'var(--surface-3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client / Assuré</th>
                                    <th style={{ padding: '12px 20px', background: 'var(--surface-3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Véhicule</th>
                                    <th style={{ padding: '12px 20px', background: 'var(--surface-3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Immatriculation</th>
                                    <th style={{ padding: '12px 20px', background: 'var(--surface-3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date Accident</th>
                                    <th style={{ padding: '12px 20px', background: 'var(--surface-3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Statut</th>
                                    <th style={{ padding: '12px 20px', background: 'var(--surface-3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>PV</th>
                                    <th style={{ padding: '12px 20px', background: 'var(--surface-3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(sinistres) && sinistres.map((s) => (
                                    <tr key={s.id} className="table-row-hover" style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '15px 20px' }}>
                                            <div style={{ fontWeight: '900', color: 'var(--primary-700)', fontSize: '14px', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                                                {s.reference_interne || '—'}
                                            </div>
                                            {s.numero_sinistre && (
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: '3px' }}>
                                                    N° Sin.Assur: {s.numero_sinistre}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '15px 20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <UserAvatar name={s.nom_assure} />
                                                <div>
                                                    <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)' }}>{s.nom_assure || '—'}</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Police: {s.numero_police || '—'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '15px 20px' }}>
                                            <div style={{ fontSize: '13px', fontWeight: '600' }}>{[s.marque_vehicule, s.modele_vehicule].filter(Boolean).join(' ') || '—'}</div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{s.genre || '—'} {s.type_vehicule ? `(${s.type_vehicule})` : ''} • {s.annee_vehicule || '—'}</div>
                                        </td>
                                        <td style={{ padding: '15px 20px' }}>
                                            <Plate value={s.immatriculation} />
                                        </td>
                                        <td style={{ padding: '15px 20px', fontSize: '12px' }}>
                                            {formatDate(s.date_sinistre)}
                                        </td>
                                        <td style={{ padding: '15px 20px' }}>
                                            <span className={`badge ${s.statut === 'brouillon' ? 'badge-orange' :
                                                s.statut === 'en_cours' ? 'badge-blue' :
                                                    s.statut === 'termine' ? 'badge-green' : 'badge-gray'
                                                }`}>
                                                {s.statut === 'brouillon' ? '📝 Brouillon' :
                                                    s.statut === 'en_cours' ? '⌛ En cours' :
                                                        s.statut === 'termine' ? '✅ Terminé' : s.statut || '—'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '15px 20px', textAlign: 'center' }}>
                                            {(s.nb_rapports || 0) > 0 ? (
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--success)', fontWeight: '800', fontSize: '13px' }}>
                                                    <FileCheck size={14} /> OK
                                                </div>
                                            ) : (
                                                <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '15px 20px', textAlign: 'right' }}>
                                            <div className="table-actions" style={{ justifyContent: 'flex-end' }}>
                                                <button className="btn btn-ghost btn-sm action-view" title="Consulter"
                                                    onClick={() => navigate(`/sinistres/${encodeId(s.id)}`)}>
                                                    <Eye size={18} />
                                                </button>
                                                {s.source_table === 'sinistres' && (
                                                    <button className="btn btn-ghost btn-sm action-edit" title="Modifier"
                                                        onClick={() => navigate(`/sinistres/${encodeId(s.id)}/modifier`)}>
                                                        <Pencil size={18} />
                                                    </button>
                                                )}
                                                {(s.nb_rapports || 0) > 0 ? (
                                                    <button className="btn btn-ghost btn-sm action-view" title="Voir le PV existant"
                                                        onClick={() => navigate(`/rapports/${s.rapport_id}`)}>
                                                        <FileCheck size={18} />
                                                    </button>
                                                ) : (
                                                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--gold-500)' }} title="Créer PV"
                                                        onClick={() => navigate(`/rapports/nouveau?sinistre=${encodeId(s.id)}`)}>
                                                        <FileText size={18} />
                                                    </button>
                                                )}
                                                <button className="btn btn-ghost btn-sm action-delete" title="Supprimer"
                                                    onClick={() => handleDeleteRequest(s.id, s.reference_interne || s.numero_sinistre)}>
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Smart Pagination */}
                <SmartPagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    limit={pagination.limit}
                    onPageChange={(p) => fetchSinistres(p)}
                />
            </div>
            {/* AI Scanning Modal */}
            <AISinistreModal 
                open={aiModalOpen} 
                onClose={() => setAiModalOpen(false)} 
                onScanned={(data) => navigate('/sinistres/nouveau', { state: data })}
            />
        </Layout>
    );
};

export default SinistresPage;
